import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { getMaxRealEstateValue } from "@component/Mortgage/Calculator/domain";
import { currentYear } from "@shared/lib/current-year";
import type { EventComponentType } from "@shared/lib/google-analytics-4.type";
import { JsonDataBuilder } from "@shared/lib/JsonDataBuilder";
import { logError } from "@shared/lib/sentry-error";
import { formatText } from "@shared/ui/format";
import type { ReactNode } from "react";
import {
	calculateCSOKAmount,
	calculateCSOKLoanTotal,
	calculateCSOKMonthlyInstallmentFirstYear,
	calculateCSOKMonthlyInstallmentSecondYear,
	calculateMarketLoanMonthlyInstallment,
	calculateMarketLoanTotal,
	calculateTotalDifference,
	getKoText,
} from "./domain";
import * as Step from "./Steps";
import type { CsokPlusCalcRequest, CsokPlusCalcResponse, FormFields } from "./types";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	leadformResponse: any;
	koMessage?: string;
}

/**
 * @Shortcode
 *
 * @title "CSOK Plusz"
 * @shortcode "csok-plus-small-calculator"
 * @slug "csok-plusz"
 * @type "calculator"
 */
export class CsokPlusSmallCalculator extends AbstractLeadForm<
	CsokPlusCalcRequest,
	CsokPlusCalcResponse,
	FormFields,
	AbstractLeadFormProps<CsokPlusCalcRequest, CsokPlusCalcResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "MORTGAGE-CSOK_PLUS";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: `CSOK Plusz Kalkulátor ${currentYear()}`,
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			leadformResponse: undefined,
			koMessage: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const { consultant, source } = this.props;

		const {
			values: { email, isMarried, womanAge, realEstateValue, maturityYears, loanAmount, wantsFirstHouse },
			koMessage,
			submitState,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" />
				<Step.Step2 stepName="step-2" defaultTarget="step-3" canStepBack />
				<Step.Result
					stepName="step-3"
					defaultTarget="lead-form"
					canStepBack
					isMarried={isMarried}
					loanAmount={loanAmount}
					maturityYears={maturityYears}
					womanAge={womanAge}
					realEstateValue={realEstateValue}
					wantsFirstHouse={wantsFirstHouse}
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					button="Elküldöm"
					submitState={submitState}
					colorVersion="white"
					consultant={consultant}
					source={source}
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
				<Step.KoPage stepName="ko-page" canStepBack koMessage={koMessage} />
				<Step.JtmKoPage stepName="jtm-ko-page" canStepBack />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const {
			values: {
				isMarried,
				wantsFirstHouse,
				realEstateValue,
				currentChildren,
				plannedChildren,
				loanAmount,
				maturityYears,
				monthlyIncome,
				qualifiesForHigherLtv,
				targetPropertyAcceptableType,
			},
		} = this.state;

		switch (target) {
			case "success-page":
				this.onSuccessPage();
				break;

			case "step-3": {
				const koMessage = getKoText({
					currentChildren,
					monthlyIncome,
					plannedChildren,
					loanAmount,
					realEstateValue,
					isMarried,
					wantsFirstHouse,
					maturityYears,
					qualifiesForHigherLtv,
					targetPropertyAcceptableType,
				});

				this.setState({ koMessage });
				this.sendCalculationEvent(koMessage ? [koMessage] : []);

				if (koMessage) {
					if (koMessage === "JTM-KO") {
						super.onStep("jtm-ko-page", withEvent);
					} else {
						super.onStep("ko-page", withEvent);
					}
				} else {
					super.onStep(target, withEvent);
				}
				break;
			}

			default:
				super.onStep(target, withEvent);
		}
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});
		this.sendLead()
			.then((_response) => {
				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getCalculatorRequest(): CsokPlusCalcRequest {
		const {
			values: { loanAmount, maturityYears, realEstateValue, monthlyIncome, requestConsultantCall },
		} = this.state;

		return {
			...super.getCalculatorRequest(),

			requestConsultantCall,
			realEstateValue,
			loanAmount,
			maturity: maturityYears,
			incomeList: [
				{
					incomeType: "SALARY-H",
					incomeTypeName: "Magyar alkalmazotti jövedelem",
					amount: monthlyIncome,
					receiptAmount: monthlyIncome,
				},
			],
		};
	}

	protected getLeadDataRequest(): CsokPlusCalcRequest {
		const {
			values: {
				loanAmount,
				maturityYears,
				monthlyIncome,
				realEstateValue,
				isMarried,
				womanAge,
				currentChildren,
				plannedChildren,
				wantsFirstHouse,
				targetPropertyAcceptableType,
			},
		} = this.state;

		const request = this.getCalculatorRequest();
		const otherData = new JsonDataBuilder(request.otherDataJson);
		const targetPropertyAcceptableTypeString = targetPropertyAcceptableType === "LAKAS" ? "lakás" : "lakóház";

		// Input
		otherData.addItem("Hitelösszeg", "loanAmount", loanAmount, "ft");
		otherData.addItem("Futamidő", "maturityYears", maturityYears);
		otherData.addItem("Havi jövedelem", "monthlyIncome", monthlyIncome, "ft");
		otherData.addItem("Ingatlan értéke", "realEstateValue", realEstateValue, "ft");
		otherData.addItem("Házasok?", "married", isMarried, "bool");
		otherData.addItem("Hölgy életkora", "womanAge", womanAge);
		otherData.addItem("Meglévő gyermekek száma:", "numberOfChildren", currentChildren);
		otherData.addItem("Tervezett gyermekek száma:", "numberOfChildrenCommitment", plannedChildren);
		otherData.addItem("Első ingatlant vásárolnak?", "wantsFirstHouse", wantsFirstHouse, "bool");
		otherData.addItem("Ingatlan típusa", "targetPropertyAcceptableType", targetPropertyAcceptableTypeString);

		return {
			...request,
			maturity: maturityYears,
			otherDataJson: otherData.getJson(),
		};
	}

	protected getCalculatorResponse(): CsokPlusCalcResponse {
		return {
			...super.getCalculatorResponse(),
			interestPeriod: 5 * 12,
		};
	}

	public getLeadData(): LeadRequest<CsokPlusCalcRequest, CsokPlusCalcResponse> {
		const data = super.getLeadData();

		data.productSubType = "CSOK_PLUS";
		data.administrationLocation = data.cityId;
		data.cityId = undefined;

		return data;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-csok-plusz";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const data = this.getLeadData();
		const {
			values: {
				realEstateValue,
				wantsFirstHouse,
				currentChildren,
				plannedChildren,
				loanAmount,
				maturityYears,
				targetPropertyAcceptableType,
			},
		} = this.state;

		const maxRealEstate = getMaxRealEstateValue({
			onlyCsokPlus: true,
			wantsFirstHouse,
			targetPropertyAcceptableType,
		});

		return {
			lastName: data.lastName,
			firstName: data.firstName,

			maxRealEstateInMillions: maxRealEstate / 1_000_000,
			maxAvailableLoanInMillions: (calculateCSOKAmount(currentChildren, plannedChildren) ?? 0) / 1_000_000,
			dutyFreeSavings: formatText(realEstateValue * 0.04, "ft"),
			realEstateValueNumber: realEstateValue,
			csokPlusSavings: formatText(calculateTotalDifference(maturityYears * 12, loanAmount), "ft"),
			allRecoveredMarketLoan: formatText(calculateMarketLoanTotal(maturityYears * 12, loanAmount), "ft"),
			installmentMarketLoan: formatText(
				calculateMarketLoanMonthlyInstallment(maturityYears * 12, loanAmount),
				"ft",
			),
			allRecovered: formatText(calculateCSOKLoanTotal(maturityYears * 12, loanAmount), "ft"),
			installmentSecondYear: formatText(
				calculateCSOKMonthlyInstallmentSecondYear(maturityYears * 12, loanAmount),
				"ft",
			),
			installmentFirstYear: formatText(calculateCSOKMonthlyInstallmentFirstYear(loanAmount), "ft"),
			durationYears: maturityYears,
			loanAmount: formatText(loanAmount, "ft"),
			realEstateValue: formatText(realEstateValue, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "MORTGAGE";
	}

	protected getComponentName(): string {
		return "CsokPlusSmallCalculator";
	}
}

export default CsokPlusSmallCalculator;
